import React, { useEffect, useState } from "react";
import BoxCard from "shared/components/box/BoxCard.js";
import LoadingSquare from "shared/components/loading/LoadingSquare.js";
import ControllerMatchType from "shared/components/controllers/ControllerMatchType.js";
import BoxMessage from "shared/components/box/BoxMessage.js";
import ChartLineMatchPerformance from "shared/components/charts/ChartLineMatchPerformance.js";
import ItemRowMatch from "shared/components/items/ItemRowMatch";
import ControllerSeasons from "shared/components/controllers/ControllerSeasons.js";
import ButtonSelect from "shared/components/buttons/ButtonSelect.js";
import { computeMatchRate } from "shared/utils/rate.js";
import { getMatches, getOverallVsGdRates } from "shared/services/api-assistant.js";

const MiscClubPerformance = ({ clubId, clubType }) => {
  const isArenaClub = clubType === "ARENA";
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [matches, setMatches] = useState(null);
  const [types, setTypes] = useState(["LEAGUE", "CUP"]);
  const [arenaMatchScope, setArenaMatchScope] = useState("initiated");
  const [overallVsGdRates, setOverallVsGdRates] = useState(null);

  const fetchOverallVsGdRates = () => {
    getOverallVsGdRates({
      handleSuccess: (d) => setOverallVsGdRates(d.data.getOverallVsGdRates),
      handleError: console.log,
      params: { engine: "15" },
    });
  };

  const fetchMatches = () => {
    if (!clubId || !selectedCompetition) return;

    setMatches(null);

    getMatches({
      handleSuccess: (d) => setMatches(d.data.getMatches),
      handleError: console.log,
      params: {
        limit: 50,
        clubs: [clubId],
        homeOnly: isArenaClub && arenaMatchScope === "initiated",
        statuses: ["LIVE", "ENDED"],
        types: isArenaClub ? ["ARENA"] : types,
        minDate: selectedCompetition?.startingDate?.slice(0, 10),
        maxDate: endDate?.slice(0, 10),
      },
    });
  };

  useEffect(() => {
    fetchOverallVsGdRates();
    if (clubId && selectedCompetition) {
      fetchMatches();
    }
  }, []);

  useEffect(() => {
    if (clubId && selectedCompetition) {
      fetchMatches();
    }
  }, [clubId, isArenaClub, selectedCompetition, types, arenaMatchScope]);

  const isHomeClub = (match) => match.homeClub.id === clubId;

  const computeAverageRate = (count) => {
    if (!matches || !overallVsGdRates) return null;

    const selectedMatches = matches.slice(0, Math.min(count, matches.length));

    const rates = selectedMatches
      .map((m) => computeMatchRate(m, overallVsGdRates, isHomeClub(m)))
      .filter((r) => typeof r === "number");

    if (!rates.length) return null;

    const avg = rates.reduce((acc, r) => acc + r, 0) / rates.length;
    return Math.round(avg * 10) / 10;
  };

  return (
    <div className="d-flex flex-column">
      <div className="d-flex flex-column flex-md-row">
        <div className="d-flex flex-grow-0 flex-fill">
          <BoxCard
            className="fit-content flex-fill"
            title="Season"
            content={
              <div className="d-flex justify-content-center mt-2 mb-1 w-100">
                <ControllerSeasons
                  onChange={(c, d) => {
                    setSelectedCompetition(c);
                    setEndDate(d);
                  }}
                />
              </div>
            }
          />
        </div>

        <div className="d-flex flex-grow-1">
          <BoxCard
            className="fit-content flex-fill"
            title="Performance"
            content={
              <div className="d-flex flex-column flex-md-row flex-fill">
                {matches === null && (
                  <div className="w-100" style={{ height: 50 }}>
                    <LoadingSquare />
                  </div>
                )}

                {matches?.length > 0 && (
                  <div className="d-flex flex-column flex-md-row flex-fill">
                    <div className="d-flex flex-column flex-fill" style={{ minWidth: "150px" }}>
                      <div className="d-flex flex-row flex-fill">
                        <div>Season</div>
                        <div className="d-flex flex-fill justify-content-end me-2">
                          <b>{computeAverageRate(10000)}</b>
                        </div>
                      </div>

                      <div className="d-flex flex-fill">
                        <div>Last 5</div>
                        <div className="d-flex flex-fill justify-content-end me-2">
                          <b>{computeAverageRate(5)}</b>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex flex-column flex-fill w-100" style={{ height: 50 }}>
                      <ChartLineMatchPerformance
                        matches={matches}
                        overallVsGdRates={overallVsGdRates}
                        computeMatchRate={computeMatchRate}
                        clubs={[{ id: clubId }]}
                      />
                    </div>
                  </div>
                )}

                {matches?.length === 0 && <BoxMessage className="mt-2 mb-3" content="No match found" />}
              </div>
            }
          />
        </div>
      </div>

      <BoxCard
        title="Matches"
        actions={
          <div className="d-flex h-100 align-items-center">
            {isArenaClub ? (
              <ButtonSelect
                ariaLabel="Arena matches"
                selected={arenaMatchScope}
                onChange={setArenaMatchScope}
                buttons={{ initiated: "Initiated only", all: "All matches" }}
              />
            ) : (
              <ControllerMatchType selectedCriteria={types} onChange={(t) => setTypes(t)} />
            )}
          </div>
        }
        content={
          <div className="d-flex flex-column flex-fill mt-1">
            {overallVsGdRates && matches ? (
              matches.length > 0 ? (
                matches.map((m) => (
                  <ItemRowMatch
                    key={m.id}
                    match={m}
                    rate={m.status === "ENDED" ? computeMatchRate(m, overallVsGdRates, isHomeClub(m)) : "?"}
                  />
                ))
              ) : (
                <div className="d-flex flex-fill w-100" style={{ height: 200 }}>
                  <BoxMessage content="No match found" />
                </div>
              )
            ) : (
              <div className="d-flex flex-fill w-100" style={{ height: 200 }}>
                <LoadingSquare />
              </div>
            )}
          </div>
        }
      />
    </div>
  );
};

export default MiscClubPerformance;
